import { Dictionary, map, size } from "lodash";
import { DeliveryOrder } from "../../../../model/delivery-order";
import { DeliveryOrderDetail } from "../../../../model/delivery-order-detail";
import { ParcelDetail } from "../../../../model/parcel-detail";
import { Parcel } from "../../../../model/parcel";
import { t } from "i18next";
import { UploadedStockout } from "./uploaded-stockout";

export class UploadedParcel {
    private readonly _id?: number;
    private readonly _tracker?: string;
    private readonly _carrierName?: string;
    private readonly _contents: Dictionary<number>;
    private readonly _stockout: UploadedStockout[];

    constructor(contents: Dictionary<number>, stockout: UploadedStockout[], tracker?: string, carrierName?: string, id?: number) {
        this._id = id;
        this._tracker = tracker;
        this._carrierName = carrierName;
        this._contents = contents;
        this._stockout = stockout;

        if (this.carrierNameOrEmpty === "" && this.trackerOrEmpty !== "") {
            throw new Error(t("bulkParcelUpload.error.trackingDataWithoutCarrierViolation", {
                parcelTracker: this.trackerOrEmpty,
            }));
        }

        if (this._stockout.length > 0 && this._id === undefined) {
            throw new Error("Invalid arguments: Unable to perform parcel stockout without parcel provided.");
        }
    }

    get id(): number | undefined {
        return this._id;
    }

    get contents(): Dictionary<number> {
        return this._contents;
    }

    get stockout(): UploadedStockout[] {
        return this._stockout;
    }

    get carrierNameOrEmpty(): string {
        return this._carrierName ?? "";
    }

    get trackerOrEmpty(): string {
        return this._tracker ?? "";
    }

    public get isSubmitted(): boolean {
        return this._id !== undefined;
    }

    public isUploadedContentUpdated(currentContent: ParcelDetail[]): boolean {
        return currentContent.length !== size(this._contents) ||
            currentContent.some(op => this._contents[op.productId] !== op.quantity);
    }

    public toParcelDetails(deliveryOrder: DeliveryOrder): ParcelDetail[] {
        return map(this._contents,
            (quantity: number, productId: string) => this.buildParcelDetail(deliveryOrder.getDetailByProductId(Number(productId)), quantity));
    }

    private buildParcelDetail(deliveryOrderDetail: DeliveryOrderDetail, quantity: number): ParcelDetail {
        const parcelDetail = new ParcelDetail();

        parcelDetail.assignDeliveryOrderDetailData(deliveryOrderDetail);
        parcelDetail.quantity = quantity;
        parcelDetail.initialQuantity = quantity;

        return parcelDetail;
    }

    public isCarrierUpdatedForParcel(parcel: Parcel) {
        return parcel.carrier !== this.carrierNameOrEmpty;
    }

    public isParcelTrackerUpdatedForParcel(parcel: Parcel) {
        return parcel.parcelTracker !== this.trackerOrEmpty;
    }

    public isParcelTrackerOrCarrierRemovedFromParcel(parcel: Parcel) {
        return parcel.parcelTracker !== "" && this.trackerOrEmpty === "" ||
            parcel.carrier !== "" && this.carrierNameOrEmpty === "";
    }

    public isParcelTrackerValid(deliveryOrder: DeliveryOrder, parcel: Parcel) {
        return [
            deliveryOrder.orderId.toString(),
            deliveryOrder.deliveryOrderId.toString(),
            parcel.id?.toString() ?? "",
            `VP${deliveryOrder.orderId}`,
            `VP${deliveryOrder.deliveryOrderId}`,
            `VP${parcel.id ?? ""}`,
        ].every(f => f !== this._tracker);
    }
}
